<template>
  <div class="page">
    <div class="seiten-kopf">
      <router-link to="/einkaufsliste" class="seiten-kopf-zurueck" aria-label="Zurück">
        <font-awesome-icon icon="fa-solid fa-arrow-left" />
      </router-link>
      <h1>Zutaten anzeigen</h1>
    </div>

    <p v-if="Status" class="hint-message success">{{ Status }}</p>
    <p v-if="Fehlermeldung" class="error-message">{{ Fehlermeldung }}</p>

    <p v-if="Zutaten.length === 0" class="hint-message">Noch keine Zutaten vorhanden.</p>

    <template v-else>
      <div class="liste-suche">
        <input v-model="Suche" type="text" placeholder="Suchen..." class="liste-suche-feld" />
        <router-link
          to="/einkaufsliste/zutaten-duplikate"
          class="filter-btn"
          aria-label="Duplikate finden"
          title="Duplikate finden"
        >
          <font-awesome-icon icon="fa-solid fa-code-merge" />
        </router-link>
        <button
          type="button"
          class="filter-btn"
          :class="{ 'filter-btn--aktiv': FilterAnzahl > 0 }"
          aria-label="Filtern"
          title="Filtern"
          @click="FilterOffen = true"
        >
          <font-awesome-icon icon="fa-solid fa-filter" />
          <span v-if="FilterAnzahl > 0" class="filter-btn-badge">{{ FilterAnzahl }}</span>
        </button>
        <button type="button" class="btn btn-secondary liste-falten-btn" @click="FoldUnfoldAlle">
          {{ AlleEingeklappt ? 'Ausklappen' : 'Einklappen' }}
        </button>
      </div>
      <p v-if="Gruppen.length === 0" class="hint-message">Keine Einträge gefunden.</p>
      <div v-else class="eintrag-liste-scroll">
        <div class="kategorie-gruppe" v-for="Gruppe in Gruppen" :key="Gruppe.Kategorie">
          <h2 class="kategorie-titel" @click="KategorieToggeln(Gruppe.Kategorie)">
            <span>{{ Gruppe.Kategorie }}</span>
            <span class="kategorie-badge">{{ Gruppe.Eintraege.length }}</span>
            <font-awesome-icon icon="fa-solid fa-chevron-right" class="kategorie-pfeil" :class="{ 'kategorie-pfeil--offen': !GeklapptKategorien.includes(Gruppe.Kategorie) }" />
          </h2>
          <div v-show="!GeklapptKategorien.includes(Gruppe.Kategorie)" class="eintrag-liste">
            <div class="eintrag-zeile" v-for="Zutat in Gruppe.Eintraege" :key="Zutat.Id">
              <div
                class="eintrag-mit-bild eintrag-mit-bild--klickbar"
                role="button"
                tabindex="0"
                @click="VerwendungOeffnen(Zutat)"
                @keydown.enter="VerwendungOeffnen(Zutat)"
              >
                <img
                  v-if="Zutat.HatBild"
                  :src="ZutatBildUrl(Zutat)"
                  class="mini-bild"
                  alt=""
                  loading="lazy"
                  @error="$event.target.style.display = 'none'"
                />
                <div class="eintrag-info">
                  <span class="eintrag-name">{{ Zutat.Name }}</span>
                  <span class="eintrag-details">
                    {{ MengeAnzeigen(Zutat.Menge) }} {{ Zutat.Einheit }}
                    <template v-if="RezepteMitZutat(Zutat.Id).length > 0">
                      · in {{ RezepteMitZutat(Zutat.Id).length }} {{ RezepteMitZutat(Zutat.Id).length === 1 ? 'Rezept' : 'Rezepten' }}
                    </template>
                  </span>
                </div>
              </div>
              <div class="eintrag-aktionen">
                <router-link :to="`/einkaufsliste/zutaten-bearbeiten/${Zutat.Id}`" class="aktion-button">Bearbeiten</router-link>
                <button type="button" class="aktion-button loeschen" @click="Loeschen(Zutat)">Zutat löschen</button>
                <button type="button" class="aktion-button hinzufuegen" @click="ZurEinkaufslisteHinzufuegen(Zutat)">Zur Einkaufsliste</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>

    <!-- Ziel-Liste waehlen (nur bei mehreren Listen) -->
    <Teleport to="body">
      <div v-if="ListenwahlOffen" class="overlay" @click.self="ListenwahlOffen = false">
        <div class="bottom-sheet" role="dialog" aria-modal="true" aria-label="Einkaufsliste wählen">
          <div class="sheet-griff"></div>
          <h3 class="sheet-titel">Auf welche Einkaufsliste?</h3>
          <button
            v-for="Liste in Einkaufslisten"
            :key="Liste.Id"
            type="button"
            class="listen-option"
            :class="{ 'listen-option--aktiv': Liste.Id === AktiveEinkaufsliste.Id }"
            @click="ListeGewaehlt(Liste)"
          >
            <font-awesome-icon icon="fa-solid fa-cart-shopping" />
            {{ Liste.Name }}
          </button>
          <button type="button" class="btn btn-secondary" @click="ListenwahlOffen = false">Abbrechen</button>
        </div>
      </div>
    </Teleport>

    <!-- Verwendung in Rezepten -->
    <Teleport to="body">
      <div v-if="VerwendungOffen" class="overlay" @click.self="VerwendungOffen = false">
        <div class="bottom-sheet" role="dialog" aria-modal="true" aria-label="Verwendung in Rezepten">
          <div class="sheet-griff"></div>
          <h3 class="sheet-titel">{{ VerwendungZutat && VerwendungZutat.Name }}</h3>
          <p class="sheet-untertitel">Verwendet in:</p>

          <div v-if="VerwendungRezepte.length > 0" class="verwendung-liste">
            <router-link
              v-for="Rezept in VerwendungRezepte"
              :key="Rezept.Id"
              :to="`/rezepte/bearbeiten/${Rezept.Id}`"
              class="verwendung-eintrag"
              @click="VerwendungOffen = false"
            >
              <img
                v-if="Rezept.HatBild"
                :src="RezeptBildUrl(Rezept)"
                class="mini-bild"
                alt=""
                loading="lazy"
                @error="$event.target.style.display = 'none'"
              />
              <span class="verwendung-info">
                <span class="verwendung-name">{{ Rezept.Name }}</span>
                <span class="verwendung-details">{{ Rezept.Kategorie }} · {{ ZutatMengeImRezept(Rezept) }}</span>
              </span>
              <font-awesome-icon icon="fa-solid fa-chevron-right" class="verwendung-pfeil" />
            </router-link>
          </div>
          <p v-else class="hint-message">Wird in keinem Rezept verwendet.</p>

          <button type="button" class="btn btn-secondary" @click="VerwendungOffen = false">Schließen</button>
        </div>
      </div>
    </Teleport>

    <!-- Filtern -->
    <Teleport to="body">
      <div v-if="FilterOffen" class="overlay" @click.self="FilterOffen = false">
        <div class="bottom-sheet" role="dialog" aria-modal="true" aria-label="Zutaten filtern">
          <div class="sheet-griff"></div>
          <h3 class="sheet-titel">Zutaten filtern</h3>

          <div class="filter-abschnitt">
            <span class="filter-abschnitt-titel">Kategorie</span>
            <div class="filter-chip-reihe">
              <button
                v-for="Kategorie in Kategorien"
                :key="Kategorie.Id"
                type="button"
                class="filter-chip"
                :class="{ 'filter-chip--aktiv': FilterKategorieIds.includes(Kategorie.Id) }"
                @click="FilterKategorieToggeln(Kategorie.Id)"
              >
                {{ Kategorie.Name }}
              </button>
              <button
                type="button"
                class="filter-chip"
                :class="{ 'filter-chip--aktiv': FilterKategorieIds.includes('ohne') }"
                @click="FilterKategorieToggeln('ohne')"
              >
                Ohne Kategorie
              </button>
            </div>
          </div>

          <div class="filter-abschnitt">
            <span class="filter-abschnitt-titel">Foto</span>
            <div class="filter-chip-reihe">
              <button
                type="button"
                class="filter-chip"
                :class="{ 'filter-chip--aktiv': FilterBild === null }"
                @click="FilterBild = null"
              >
                Alle
              </button>
              <button
                type="button"
                class="filter-chip"
                :class="{ 'filter-chip--aktiv': FilterBild === 'mit' }"
                @click="FilterBild = 'mit'"
              >
                Mit Foto
              </button>
              <button
                type="button"
                class="filter-chip"
                :class="{ 'filter-chip--aktiv': FilterBild === 'ohne' }"
                @click="FilterBild = 'ohne'"
              >
                Ohne Foto
              </button>
            </div>
          </div>

          <div class="filter-abschnitt">
            <span class="filter-abschnitt-titel">Eingefroren</span>
            <div class="filter-chip-reihe">
              <button
                type="button"
                class="filter-chip"
                :class="{ 'filter-chip--aktiv': FilterEingefroren === null }"
                @click="FilterEingefroren = null"
              >
                Alle
              </button>
              <button
                type="button"
                class="filter-chip"
                :class="{ 'filter-chip--aktiv': FilterEingefroren === 'eingefroren' }"
                @click="FilterEingefroren = 'eingefroren'"
              >
                🧊 Eingefroren
              </button>
              <button
                type="button"
                class="filter-chip"
                :class="{ 'filter-chip--aktiv': FilterEingefroren === 'nicht' }"
                @click="FilterEingefroren = 'nicht'"
              >
                Nicht eingefroren
              </button>
            </div>
          </div>

          <button v-if="FilterAnzahl > 0" type="button" class="filter-zuruecksetzen" @click="FilterZuruecksetzen">
            Filter zurücksetzen ({{ FilterAnzahl }})
          </button>

          <button type="button" class="btn btn-primary" @click="FilterOffen = false">Fertig</button>
        </div>
      </div>
    </Teleport>

  </div>
</template>

<script>
import {
  Zutaten,
  ZutatenLaden,
  Kategorien,
  KategorienLaden,
  ZutatLoeschen,
  ZutatZurEinkaufslisteHinzufuegen,
  Einkaufslisten,
  EinkaufslistenLaden,
  AktiveEinkaufsliste
} from '../composables/useZutaten'
import { Rezepte, RezepteLaden } from '../composables/useRezepte'
import { BildUrl } from '../composables/useBilder'

export default {
  name: 'ZutatenAnzeigenView',
  data() {
    return {
      Zutaten,
      Kategorien,
      Rezepte,
      Einkaufslisten,
      AktiveEinkaufsliste,
      Status: '',
      Fehlermeldung: '',
      Suche: '',
      GeklapptKategorien: [],
      ListenwahlOffen: false,
      ListenwahlZutat: null,
      VerwendungOffen: false,
      VerwendungZutat: null,
      FilterOffen: false,
      FilterKategorieIds: [],
      FilterBild: null,
      FilterEingefroren: null
    }
  },
  async created() {
    await Promise.all([KategorienLaden(), ZutatenLaden(), EinkaufslistenLaden(), RezepteLaden()])
  },
  computed: {
    GefilterteZutaten() {
      const s = this.Suche.toLowerCase().trim()
      return this.Zutaten
        .filter(z => !s || z.Name.toLowerCase().includes(s))
        .filter(z => !this.FilterBild || (this.FilterBild === 'mit') === !!z.HatBild)
        .filter(z => !this.FilterEingefroren || (this.FilterEingefroren === 'eingefroren') === !!z.Eingefroren)
    },
    FilterAnzahl() {
      return this.FilterKategorieIds.length + (this.FilterBild ? 1 : 0) + (this.FilterEingefroren ? 1 : 0)
    },
    AlleEingeklappt() {
      return this.Gruppen.length > 0 && this.GeklapptKategorien.length >= this.Gruppen.length
    },
    Gruppen() {
      const Basis = this.GefilterteZutaten
      // Kategorie-Filter bestimmt, welche Kategorie-Gruppen ueberhaupt betrachtet werden
      // (leer = alle, wie bisher); die Zuordnung der Zutaten zu Gruppen bleibt unveraendert.
      const AktiveKategorien = this.FilterKategorieIds.length === 0
        ? Kategorien
        : Kategorien.filter(K => this.FilterKategorieIds.includes(K.Id))

      const Gruppen = AktiveKategorien
        .map(Kategorie => ({
          Kategorie: Kategorie.Name,
          Eintraege: Basis.filter(Zutat => Zutat.KategorieId === Kategorie.Id)
        }))
        .filter(Gruppe => Gruppe.Eintraege.length > 0)

      const OhneKategorieErlaubt = this.FilterKategorieIds.length === 0 || this.FilterKategorieIds.includes('ohne')
      if (OhneKategorieErlaubt) {
        const OhneKategorie = Basis.filter(Zutat => !Zutat.KategorieId)
        if (OhneKategorie.length > 0) {
          Gruppen.push({ Kategorie: 'Ohne Kategorie', Eintraege: OhneKategorie })
        }
      }

      return Gruppen
    },
    VerwendungRezepte() {
      if (!this.VerwendungZutat) return []
      return this.RezepteMitZutat(this.VerwendungZutat.Id)
    }
  },
  methods: {
    KategorieToggeln(Name) {
      const Index = this.GeklapptKategorien.indexOf(Name)
      if (Index === -1) {
        this.GeklapptKategorien.push(Name)
      } else {
        this.GeklapptKategorien.splice(Index, 1)
      }
    },
    FoldUnfoldAlle() {
      if (this.AlleEingeklappt) {
        this.GeklapptKategorien = []
      } else {
        this.GeklapptKategorien = this.Gruppen.map(g => g.Kategorie)
      }
    },
    MengeAnzeigen(Menge) {
      return Menge != null ? +Menge : ''
    },
    ZutatBildUrl(Zutat) {
      return BildUrl('Zutat', Zutat.Id, Zutat.BildVersion)
    },
    RezeptBildUrl(Rezept) {
      return BildUrl('Rezept', Rezept.Id, Rezept.BildVersion)
    },
    RezepteMitZutat(ZutatId) {
      return this.Rezepte.filter(Rezept => (Rezept.Zutaten || []).some(Zeile => Zeile.ZutatId === ZutatId))
    },
    ZutatMengeImRezept(Rezept) {
      const Zeile = (Rezept.Zutaten || []).find(Z => Z.ZutatId === this.VerwendungZutat.Id)
      if (!Zeile || Zeile.Menge == null) return 'ohne Mengenangabe'
      return `${+Zeile.Menge} ${Zeile.Einheit || ''}`.trim()
    },
    VerwendungOeffnen(Zutat) {
      this.VerwendungZutat = Zutat
      this.VerwendungOffen = true
    },
    FilterKategorieToggeln(Id) {
      const Index = this.FilterKategorieIds.indexOf(Id)
      if (Index === -1) {
        this.FilterKategorieIds.push(Id)
      } else {
        this.FilterKategorieIds.splice(Index, 1)
      }
    },
    FilterZuruecksetzen() {
      this.FilterKategorieIds = []
      this.FilterBild = null
      this.FilterEingefroren = null
    },
    async Loeschen(Zutat) {
      this.Fehlermeldung = ''
      if (confirm(`"${Zutat.Name}" wirklich aus der Zutatenliste löschen?`)) {
        try {
          await ZutatLoeschen(Zutat.Id)
        } catch (Fehler) {
          this.Fehlermeldung = Fehler.message
        }
      }
    },
    ZurEinkaufslisteHinzufuegen(Zutat) {
      // Bei nur einer Liste ist das Ziel eindeutig - sonst kurz nachfragen
      if (this.Einkaufslisten.length <= 1) {
        this.AufListeSetzen(Zutat, this.AktiveEinkaufsliste.Id, '')
        return
      }
      this.ListenwahlZutat = Zutat
      this.ListenwahlOffen = true
    },
    ListeGewaehlt(Liste) {
      this.ListenwahlOffen = false
      this.AufListeSetzen(this.ListenwahlZutat, Liste.Id, ` („${Liste.Name}")`)
    },
    async AufListeSetzen(Zutat, EinkaufslisteId, ListenZusatz) {
      this.Fehlermeldung = ''
      try {
        const Ergebnis = await ZutatZurEinkaufslisteHinzufuegen(Zutat.Id, EinkaufslisteId)
        this.Status = Ergebnis.BereitsVorhanden
          ? `"${Zutat.Name}" ist jetzt ${Ergebnis.Anzahl}× auf der Einkaufsliste${ListenZusatz}.`
          : `"${Zutat.Name}" zur Einkaufsliste hinzugefügt${ListenZusatz}.`
        setTimeout(() => { this.Status = '' }, 2000)
      } catch (Fehler) {
        this.Fehlermeldung = Fehler.message
      }
    }
  }
}
</script>

<style scoped>
.overlay {
  position: fixed;
  inset: 0;
  background: rgba(12, 12, 16, 0.45);
  -webkit-backdrop-filter: blur(6px);
  backdrop-filter: blur(6px);
  display: flex;
  align-items: flex-end;
  z-index: 200;
  animation: Einblenden 0.25s ease both;
}

.bottom-sheet {
  background: var(--glas-stark);
  -webkit-backdrop-filter: var(--glas-blur);
  backdrop-filter: var(--glas-blur);
  border: 1px solid var(--glas-rand);
  border-radius: var(--radius-xl) var(--radius-xl) 0 0;
  padding: 12px 16px 32px;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;
  animation: SheetHoch 0.35s cubic-bezier(0.16, 1, 0.3, 1) both;
}

@media (min-width: 768px) {
  .overlay {
    justify-content: center;
    align-items: center;
  }
  .bottom-sheet {
    border-radius: var(--radius-xl);
    max-width: 420px;
    padding: 24px;
  }
  .sheet-griff { display: none; }
}

.sheet-griff {
  width: 40px;
  height: 4px;
  background: var(--color-border);
  border-radius: 2px;
  margin: 0 auto 4px;
}

.sheet-titel {
  margin: 0 0 6px;
  font-size: 1.1rem;
  font-weight: 700;
}

.listen-option {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  border: 1px solid var(--glas-rand);
  border-radius: var(--radius-md);
  background: var(--color-bg-alt);
  color: var(--color-text);
  font-family: inherit;
  font-size: 0.95rem;
  font-weight: 600;
  text-align: left;
  cursor: pointer;
  transition: border-color 0.2s ease;
}

.listen-option:hover {
  border-color: var(--color-primary);
}

.listen-option--aktiv {
  border-color: color-mix(in srgb, var(--color-primary) 45%, transparent);
}

.eintrag-mit-bild--klickbar {
  cursor: pointer;
  border-radius: var(--radius-md);
  transition: background-color 0.15s ease;
}

.eintrag-mit-bild--klickbar:hover {
  background: var(--color-bg-alt);
}

.sheet-untertitel {
  margin: 0;
  font-size: 0.85rem;
  color: var(--color-text-muted);
}

.verwendung-liste {
  display: flex;
  flex-direction: column;
  gap: 6px;
  max-height: 50vh;
  overflow-y: auto;
}

.verwendung-eintrag {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border: 1px solid var(--glas-rand);
  border-radius: var(--radius-md);
  background: var(--color-bg-alt);
  color: var(--color-text);
  text-decoration: none;
  transition: border-color 0.2s ease;
}

.verwendung-eintrag:hover {
  border-color: var(--color-primary);
}

.verwendung-info {
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
  flex: 1;
}

.verwendung-name {
  font-weight: 600;
  font-size: 0.95rem;
}

.verwendung-details {
  font-size: 0.78rem;
  color: var(--color-text-muted);
}

.verwendung-pfeil {
  flex-shrink: 0;
  color: var(--color-text-muted);
  font-size: 0.8rem;
}
</style>
